import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
  Image,
  ImageSourcePropType,
  ActivityIndicator,
  Alert,
  Linking,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { recorder } from '../services/sessionTelemetry/recorder';
import * as Notifications from 'expo-notifications';
import * as Location from 'expo-location';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '../hooks/useTheme';
import { useAppDispatch } from '../store';
import Icon from '../components/Icon';
import BrandLogo from '../components/BrandLogo';
import MedicalDisclaimerModal from '../components/MedicalDisclaimerModal';
import LocationSharingDisclosureSheet from '../components/LocationSharingDisclosureSheet';
import { SET_NAV_PERSONAS } from '../store/constants';
import { LOCATION_SHARING_OPT_IN_KEY } from '../constants/location';
import { isWeb, DESKTOP_BREAKPOINT } from '../utilities/platform';
import { permissionHelpText } from '../utilities/permission-help';

// Desktop layout — DesktopLayout (App.tsx) already paints the rounded
// "widget frame" (hairline border + drop shadow) that holds the entire
// app, both in Electron and in the RN-Web browser build. On top of that
// shell, all the onboarding needs is to keep its content from stretching
// edge-to-edge in the wide widget. A transparent max-width column does
// that without nesting a second card inside the widget frame — the prior
// inner card produced a card-in-card look and a fixed-height inner that
// clipped the Skip / Continue chrome.
const DESKTOP_CONTENT_MAX_WIDTH = 720;

export const ONBOARDING_DONE_KEY = '@eleplan/onboarding_complete';
export const BIOMETRIC_DEFERRED_KEY = '@eleplan/setup_biometric_deferred';
export const PIN_DEFERRED_KEY = '@eleplan/setup_pin_deferred';
export const ONBOARDING_ROLE_KEY = '@eleplan/onboarding_role';
export type OnboardingRole = 'self' | 'caregiver';

export async function hasSeenOnboarding(): Promise<boolean> {
  const val = await AsyncStorage.getItem(ONBOARDING_DONE_KEY);
  return val === 'true';
}

export async function markOnboardingComplete(): Promise<void> {
  await AsyncStorage.setItem(ONBOARDING_DONE_KEY, 'true');
  // Device-local tour finishing — pre-auth, so this lands on the anonymous
  // session when the prelogin flag is on (no-op otherwise).
  recorder.track('onboarding_completed');
}

const logoImage = require('../../assets/horizontal_logo.png') as ImageSourcePropType;

interface Slide {
  key: string;
  icon: [string, string];
  iconColor: string;
  iconBg: string;
  title: string;
  body: string;
  action?: 'notifications' | 'security' | 'location' | 'role' | 'interests';
}

const PERSONA_OPTIONS: { key: string; label: string; desc: string; icon: [string, string]; color: string }[] = [
  { key: 'meds', label: 'Medication Tracking', desc: 'Track doses, schedules, and adherence', icon: ['fad', 'pills'], color: '#8B5CF6' },
  { key: 'reminders', label: 'Medication Reminders', desc: 'Never miss a dose', icon: ['fad', 'bell'], color: '#F59E0B' },
  { key: 'calendar', label: 'Appointment Tracking', desc: 'Manage visits and follow-ups', icon: ['fad', 'calendar-check'], color: '#0EA5E9' },
  { key: 'docs', label: 'Document Storage', desc: 'Organize records, forms, and files', icon: ['fad', 'folder-medical'], color: '#10B981' },
  { key: 'comms', label: 'Better Communication', desc: 'Stay in sync with your care team', icon: ['fad', 'messages'], color: '#6366F1' },
  { key: 'ai', label: 'Data Intelligence', desc: 'Instant answers about your care', icon: ['fad', 'brain-circuit'], color: '#EE6C4C' },
  { key: 'benefits', label: 'Benefits & Insurance', desc: 'Understand coverage and eligibility', icon: ['fad', 'shield-halved'], color: '#64748B' },
  { key: 'family', label: 'Family Care', desc: 'Coordinate care for loved ones', icon: ['fad', 'people-group'], color: '#EC4899' },
];

const SLIDES: Slide[] = [
  {
    key: 'welcome',
    icon: ['fad', 'sparkles'],
    iconColor: '#EE6C4C',
    iconBg: 'rgba(238,108,76,0.12)',
    title: 'Intelligent care,\nwithout the chaos.',
    body: 'Every chart, medication, and record — organized into one powerful brain. Ask anything about anyone in your care, get answers instantly.',
  },
  {
    key: 'role',
    icon: ['fad', 'people-group'],
    iconColor: '#6366F1',
    iconBg: 'rgba(99,102,241,0.12)',
    title: 'Who is this for?',
    body: 'This helps us tailor your experience.',
    action: 'role',
  },
  {
    key: 'interests',
    icon: ['fad', 'sliders'],
    iconColor: '#EE6C4C',
    iconBg: 'rgba(238,108,76,0.12)',
    title: "What matters most?",
    body: "Choose what you'd like to focus on. We'll prioritize these areas in your navigation.",
    action: 'interests',
  },
  {
    // Differentiator slide. Voice intentionally mirrors SnapshotIntroSheet
    // ("device holds the key") so the user hears the same story when they
    // later open the Snapshots screen. Custom layout in the render path
    // (3 feature rows) — title/body here act as the lead-in copy.
    key: 'snapshots',
    icon: ['fad', 'lock-keyhole'],
    iconColor: '#7C3AED',
    iconBg: 'rgba(124,58,237,0.12)',
    title: 'Only you can open it.',
    body: 'Your entire health history, locked to this device with a key that never leaves it. Not even we can read it.',
  },
  {
    key: 'notifications',
    icon: ['fad', 'bell'],
    iconColor: '#F59E0B',
    iconBg: 'rgba(245,158,11,0.12)',
    title: 'Stay in the Loop',
    body: 'Enable push notifications to get care reminders, alerts, and updates the moment they matter.',
    action: 'notifications',
  },
  {
    key: 'location',
    icon: ['fad', 'location-dot'],
    iconColor: '#0EA5E9',
    iconBg: 'rgba(14,165,233,0.12)',
    title: 'Optional location sharing',
    body: 'Most useful on the recipient device for caregiver safety and last-seen updates. You can also enable this on your own device if you want to share location too.',
    action: 'location',
  },
  {
    key: 'security',
    icon: ['fad', 'shield-halved'],
    iconColor: '#EE6C4C',
    iconBg: 'rgba(238,108,76,0.12)',
    title: 'Your health data deserves protection.',
    body: 'Health records are sensitive. Choose how you want to secure your account — we\'ll set it up after you sign in.',
    action: 'security',
  },
  {
    key: 'start',
    icon: ['fad', 'rocket-launch'],
    iconColor: '#10B981',
    iconBg: 'rgba(16,185,129,0.12)',
    title: "You're All Set",
    body: 'Sign in to your existing account or create a new one to get started.',
  },
];

interface Props {
  onDone: (action?: 'login' | 'register') => void;
  mode?: 'auth' | 'replay';
}

export default function OnboardingScreen({ onDone, mode = 'auth' }: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView>(null);
  const { width: windowWidth } = useWindowDimensions();

  // On desktop the onboarding lives in a max-width column inside the
  // parent widget frame; the page width feeding the horizontal carousel
  // must match the column width so each slide snaps cleanly. On native +
  // tablet/phone web, pages are still sized to the full window so paging
  // behaves identically.
  const isDesktop = isWeb && windowWidth >= DESKTOP_BREAKPOINT;
  const pageWidth = isDesktop
    ? Math.min(DESKTOP_CONTENT_MAX_WIDTH, windowWidth)
    : windowWidth;

  const [activeIndex, setActiveIndex] = useState(0);
  const [notifStatus, setNotifStatus] = useState<'idle' | 'loading' | 'granted' | 'denied'>('idle');
  const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'granted' | 'denied'>('idle');
  const [locationDisclosureVisible, setLocationDisclosureVisible] = useState(false);
  const [biometricOptIn, setBiometricOptIn] = useState(false);
  const [pinOptIn, setPinOptIn] = useState(false);
  const [roleSelection, setRoleSelection] = useState<OnboardingRole | null>(null);
  const [personaSelection, setPersonaSelection] = useState<string[]>([]);
  const [disclaimerVisible, setDisclaimerVisible] = useState(false);

  const slides = mode === 'replay' ? SLIDES.filter((slide) => slide.key !== 'start') : SLIDES;
  const isLast = activeIndex === slides.length - 1;

  const goTo = (index: number) => {
    scrollRef.current?.scrollTo({ x: index * pageWidth, animated: true });
    setActiveIndex(index);
  };

  const handleNext = async () => {
    if (isLast) return;
    const currentAction = slides[activeIndex]?.action;

    // Notifications slide: tapping Continue triggers the native OS permission
    // prompt (if we haven't asked yet). Once the user responds — grant or deny
    // — we advance automatically. If the user has previously denied (iOS won't
    // re-prompt), or already granted, we just advance.
    if (currentAction === 'notifications' && notifStatus === 'idle') {
      await handleNotifications();
      goTo(activeIndex + 1);
      return;
    }
    // Don't double-trigger while the prompt is already in flight.
    if (currentAction === 'notifications' && notifStatus === 'loading') return;

    // Location slide: tapping Continue opens the prominent-disclosure sheet
    // (Apple 5.1.1(iv) / Google prominent-disclosure). The sheet's own
    // Continue button hands off to the OS dialog via handleLocationPermissions,
    // and onEnable auto-advances the carousel once the user has answered.
    if (currentAction === 'location' && locationStatus === 'idle') {
      setLocationDisclosureVisible(true);
      return;
    }
    if (currentAction === 'location' && locationStatus === 'loading') return;

    goTo(activeIndex + 1);
  };

  const handleSkip = async () => {
    await markOnboardingComplete();
    onDone('login');
  };

  const handleRoleSelect = async (role: OnboardingRole) => {
    setRoleSelection(role);
    await AsyncStorage.setItem(ONBOARDING_ROLE_KEY, role);
    goTo(activeIndex + 1);
  };

  const handlePersonaToggle = (key: string) => {
    setPersonaSelection((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handlePersonaContinue = async () => {
    await AsyncStorage.setItem('@eleplan/nav_personas', JSON.stringify(personaSelection));
    dispatch({ type: SET_NAV_PERSONAS, payload: personaSelection });
    goTo(activeIndex + 1);
  };

  const handleNotifications = async () => {
    setNotifStatus('loading');
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      setNotifStatus(status === 'granted' ? 'granted' : 'denied');
    } catch {
      setNotifStatus('denied');
    }
  };

  const handleSecurityToggle = async (type: 'biometric' | 'pin') => {
    if (type === 'biometric') {
      const next = !biometricOptIn;
      setBiometricOptIn(next);
      if (next) await AsyncStorage.setItem(BIOMETRIC_DEFERRED_KEY, 'true');
      else await AsyncStorage.removeItem(BIOMETRIC_DEFERRED_KEY);
    } else {
      const next = !pinOptIn;
      setPinOptIn(next);
      if (next) await AsyncStorage.setItem(PIN_DEFERRED_KEY, 'true');
      else await AsyncStorage.removeItem(PIN_DEFERRED_KEY);
    }
  };

  const handleLocationPermissions = async () => {
    setLocationStatus('loading');
    try {
      const existing = await Location.getForegroundPermissionsAsync();
      if (existing.status !== 'granted' && existing.canAskAgain === false) {
        setLocationStatus('denied');
        await AsyncStorage.setItem(LOCATION_SHARING_OPT_IN_KEY, 'false');
        // `Linking.openSettings()` only deep-links on native iOS/Android.
        // On web it's a no-op (browsers don't expose a way to jump into
        // site permissions), and on Electron it routes to the same no-op,
        // so omit the dead button on those platforms. The help text helper
        // gives platform-correct instructions (Settings → Notifications →
        // Eleplan on macOS, address-bar padlock on browser, etc.).
        Alert.alert(
          'Enable Location in Settings',
          permissionHelpText('location'),
          isWeb
            ? [{ text: 'OK' }]
            : [
                { text: 'Not Now', style: 'cancel' },
                { text: 'Open Settings', onPress: () => Linking.openSettings().catch(() => {}) },
              ],
        );
        return;
      }

      const fg = await Location.requestForegroundPermissionsAsync();
      const granted = fg.status === 'granted';
      setLocationStatus(granted ? 'granted' : 'denied');
      await AsyncStorage.setItem(LOCATION_SHARING_OPT_IN_KEY, granted ? 'true' : 'false');
    } catch {
      setLocationStatus('denied');
      await AsyncStorage.setItem(LOCATION_SHARING_OPT_IN_KEY, 'false');
    }
  };

  const handleScroll = (e: any) => {
    const idx = Math.round(e.nativeEvent.contentOffset.x / pageWidth);
    setActiveIndex(idx);
  };

  const s = StyleSheet.create({
    // Desktop-only centering frame. The parent DesktopLayout already
    // paints the rounded widget shell + drop shadow that holds the whole
    // app (Electron window chrome + RN-Web browser build alike); on top
    // of that, all this layer needs to do is keep the onboarding chrome
    // from stretching edge-to-edge on a wide window. Transparent column
    // — no border, no shadow, no rounded corners (those would nest a
    // second card inside the widget frame and read as a phone mockup).
    desktopFrame: {
      flex: 1,
      alignItems: 'center',
      backgroundColor: theme.colors.background,
    },
    desktopColumn: {
      flex: 1,
      width: '100%',
      maxWidth: DESKTOP_CONTENT_MAX_WIDTH,
    },
    root: { flex: 1, backgroundColor: theme.colors.background },
    skipRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      paddingHorizontal: 20,
      paddingBottom: 8,
    },
    skipBtn: { padding: 8 },
    skipText: { fontSize: 15, color: theme.colors.muted, fontWeight: '500' },
    page: {
      width: pageWidth,
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingVertical: 24,
    },
    iconWrap: {
      width: 120,
      height: 120,
      borderRadius: 60,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 32,
    },
    iconWrapCompact: {
      width: 84,
      height: 84,
      borderRadius: 42,
      marginBottom: 20,
    },
    featureList: {
      alignSelf: 'stretch',
      marginTop: 24,
      gap: 14,
      paddingHorizontal: 4,
    },
    featureRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    featureIcon: {
      width: 38,
      height: 38,
      borderRadius: 19,
      alignItems: 'center',
      justifyContent: 'center',
    },
    featureText: { flex: 1, paddingTop: 2 },
    featureTitle: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 2,
    },
    featureBody: {
      fontSize: 13,
      lineHeight: 18,
      color: theme.colors.muted,
    },
    title: {
      fontSize: 26,
      fontWeight: '700',
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 14,
      lineHeight: 34,
    },
    body: {
      fontSize: 16,
      color: theme.colors.muted,
      textAlign: 'center',
      lineHeight: 24,
      maxWidth: 300,
    },
    notifRow: { marginTop: 28, alignItems: 'center', gap: 10 },
    notifBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 24,
      paddingVertical: 13,
      borderRadius: 12,
      backgroundColor: 'rgba(245,158,11,0.12)',
    },
    notifBtnText: { fontSize: 15, fontWeight: '600', color: '#F59E0B' },
    notifGranted: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
    notifGrantedText: { fontSize: 14, color: theme.colors.success || '#10B981', fontWeight: '500' },
    notifSkipText: {
      fontSize: 13,
      color: theme.colors.muted,
      textAlign: 'center',
      maxWidth: 300,
      paddingHorizontal: 8,
      lineHeight: 20,
    },
    dotsRow: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      marginBottom: 16,
    },
    stepCounter: { fontSize: 12, fontWeight: '600' },
    dotsInner: { flexDirection: 'row', gap: 6 },
    dot: { width: 8, height: 8, borderRadius: 4 },
    footer: { paddingHorizontal: 24, paddingBottom: Platform.OS === 'ios' ? 20 : 28, gap: 10 },
    primaryBtn: {
      height: 54,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary,
    },
    primaryBtnText: { fontSize: 16, fontWeight: '700', color: theme.colors.primaryText },
    secondaryBtn: {
      height: 54,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1.5,
      borderColor: theme.colors.border,
    },
    secondaryBtnText: { fontSize: 16, fontWeight: '600', color: theme.colors.text },
    logo: { width: '100%', maxWidth: 320, height: 64, resizeMode: 'contain', marginBottom: 36 },
    roleRows: { marginTop: 24, gap: 12, width: '100%' },
    roleCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
      padding: 18,
      borderRadius: 16,
      borderWidth: 1.5,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.card,
    },
    roleCardActive: {
      borderColor: '#6366F1',
      backgroundColor: 'rgba(99,102,241,0.07)',
    },
    roleIconWrap: {
      width: 44,
      height: 44,
      borderRadius: 22,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(99,102,241,0.12)',
    },
    roleCardText: { flex: 1 },
    roleCardLabel: { fontSize: 16, fontWeight: '700', color: theme.colors.text, marginBottom: 3 },
    roleCardDesc: { fontSize: 13, color: theme.colors.muted, lineHeight: 18 },
    roleCheck: {
      width: 24,
      height: 24,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    roleCheckActive: { backgroundColor: '#6366F1', borderColor: '#6366F1' },
    securityRows: { marginTop: 28, gap: 10, width: '100%' },
    securityRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      padding: 16,
      borderRadius: 14,
      borderWidth: 1.5,
      borderColor: theme.colors.border,
    },
    securityRowActive: {
      borderColor: '#EE6C4C',
      backgroundColor: 'rgba(238,108,76,0.07)',
    },
    securityIconWrap: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(238,108,76,0.12)',
    },
    securityRowText: { flex: 1 },
    securityRowLabel: { fontSize: 15, fontWeight: '600', color: theme.colors.text, marginBottom: 2 },
    securityRowDesc: { fontSize: 13, color: theme.colors.muted },
    securityCheck: {
      width: 24,
      height: 24,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    securityCheckActive: {
      backgroundColor: '#EE6C4C',
      borderColor: '#EE6C4C',
    },
    disclaimerFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      marginTop: 14,
      paddingVertical: 6,
      paddingHorizontal: 12,
    },
    disclaimerFooterText: {
      fontSize: 12,
      lineHeight: 16,
      color: theme.colors.muted,
      textAlign: 'center',
      flexShrink: 1,
    },
    disclaimerFooterLink: {
      color: theme.colors.primary,
      fontWeight: '600',
    },
    personaGrid: { marginTop: 16, flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center', width: '100%' },
    personaChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 9,
      borderRadius: 20,
      borderWidth: 1.5,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.card,
    },
    personaChipActive: {
      borderColor: '#6366F1',
      backgroundColor: 'rgba(99,102,241,0.10)',
    },
    personaChipText: { fontSize: 11, fontWeight: '600', color: theme.colors.muted },
    personaChipTextActive: { color: '#6366F1' },
  });

  // The full onboarding chrome — skip row, paged carousel, dots, footer,
  // and the two pop-up modals — is the same on every platform. Desktop just
  // wraps it in a centered card so it stops sprawling across a wide window.
  const chrome = (
    <SafeAreaView style={s.root} edges={['left', 'right', 'bottom']}>
      {/* Skip button — hidden on last slide */}
      <View style={[s.skipRow, { paddingTop: isDesktop ? 12 : insets.top + 12 }]}>
        {!isLast && slides[activeIndex]?.action !== 'location' && (
          <TouchableOpacity style={s.skipBtn} onPress={handleSkip} activeOpacity={0.7}>
            <Text style={s.skipText}>Skip</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Slides */}
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onMomentumScrollEnd={handleScroll}
        style={{ flex: 1 }}
        contentContainerStyle={{ alignItems: 'stretch' }}
      >
        {slides.map((slide, i) => (
          <View key={slide.key} style={s.page}>
            {/* Logo on welcome slide only */}
            {i === 0 && (
              <BrandLogo fallback={logoImage} style={s.logo} resizeMode="contain" accessibilityLabel="Eleplan" />
            )}

            {/* Icon circle — compact on the snapshots slide to leave room for feature rows */}
            <View
              style={[
                s.iconWrap,
                slide.key === 'snapshots' && s.iconWrapCompact,
                { backgroundColor: slide.iconBg },
              ]}
            >
              <Icon
                icon={slide.icon as any}
                size={slide.key === 'snapshots' ? 40 : 56}
                color={slide.iconColor}
              />
            </View>

            <Text style={s.title}>{slide.title}</Text>
            <Text style={s.body}>{slide.body}</Text>

            {/* Snapshots slide — value-packed feature rows, matching the visual
                language of SnapshotIntroSheet so the narrative is continuous. */}
            {slide.key === 'snapshots' && (
              <View style={s.featureList}>
                <View style={s.featureRow}>
                  <View style={[s.featureIcon, { backgroundColor: 'rgba(124,58,237,0.12)' }]}>
                    <Icon icon={['fad', 'fingerprint']} size={18} color="#7C3AED" />
                  </View>
                  <View style={s.featureText}>
                    <Text style={s.featureTitle}>Locked to you</Text>
                    <Text style={s.featureBody}>Only your face or fingerprint opens the key.</Text>
                  </View>
                </View>

                <View style={s.featureRow}>
                  <View style={[s.featureIcon, { backgroundColor: 'rgba(245,158,11,0.12)' }]}>
                    <Icon icon={['fad', 'bolt']} size={18} color="#F59E0B" />
                  </View>
                  <View style={s.featureText}>
                    <Text style={s.featureTitle}>Moves with you</Text>
                    <Text style={s.featureBody}>New phone? Bring your full health history over in seconds.</Text>
                  </View>
                </View>

                <View style={s.featureRow}>
                  <View style={[s.featureIcon, { backgroundColor: 'rgba(16,185,129,0.12)' }]}>
                    <Icon icon={['fad', 'share-nodes']} size={18} color="#10B981" />
                  </View>
                  <View style={s.featureText}>
                    <Text style={s.featureTitle}>Shared on your terms</Text>
                    <Text style={s.featureBody}>Send a private one-time link. You set expiry. Revoke any time.</Text>
                  </View>
                </View>
              </View>
            )}

            {/* Role selection */}
            {slide.action === 'role' && (
              <View style={s.roleRows}>
                <TouchableOpacity
                  style={[s.roleCard, roleSelection === 'self' && s.roleCardActive]}
                  onPress={() => handleRoleSelect('self')}
                  activeOpacity={0.8}
                >
                  <View style={s.roleIconWrap}>
                    <Icon icon={['fad', 'person']} size={22} color="#6366F1" />
                  </View>
                  <View style={s.roleCardText}>
                    <Text style={s.roleCardLabel}>For myself</Text>
                    <Text style={s.roleCardDesc}>I'm managing my own care</Text>
                  </View>
                  <View style={[s.roleCheck, roleSelection === 'self' && s.roleCheckActive]}>
                    {roleSelection === 'self' && <Icon icon={['fas', 'check']} size={11} color="#fff" />}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[s.roleCard, roleSelection === 'caregiver' && s.roleCardActive]}
                  onPress={() => handleRoleSelect('caregiver')}
                  activeOpacity={0.8}
                >
                  <View style={s.roleIconWrap}>
                    <Icon icon={['fad', 'hand-holding-heart']} size={22} color="#6366F1" />
                  </View>
                  <View style={s.roleCardText}>
                    <Text style={s.roleCardLabel}>For someone I care for</Text>
                    <Text style={s.roleCardDesc}>Parent, partner, or patient</Text>
                  </View>
                  <View style={[s.roleCheck, roleSelection === 'caregiver' && s.roleCheckActive]}>
                    {roleSelection === 'caregiver' && <Icon icon={['fas', 'check']} size={11} color="#fff" />}
                  </View>
                </TouchableOpacity>
              </View>
            )}

            {/* Security opt-in */}
            {slide.action === 'security' && (
              <View style={s.securityRows}>
                <TouchableOpacity
                  style={[s.securityRow, biometricOptIn && s.securityRowActive]}
                  onPress={() => handleSecurityToggle('biometric')}
                  activeOpacity={0.8}
                >
                  <View style={s.securityIconWrap}>
                    <Icon icon={['fad', 'face-viewfinder']} size={20} color="#EE6C4C" />
                  </View>
                  <View style={s.securityRowText}>
                    <Text style={s.securityRowLabel}>Biometric Login</Text>
                    <Text style={s.securityRowDesc}>Face ID or fingerprint sign-in</Text>
                  </View>
                  <View style={[s.securityCheck, biometricOptIn && s.securityCheckActive]}>
                    {biometricOptIn && <Icon icon={['fas', 'check']} size={11} color="#fff" />}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[s.securityRow, pinOptIn && s.securityRowActive]}
                  onPress={() => handleSecurityToggle('pin')}
                  activeOpacity={0.8}
                >
                  <View style={s.securityIconWrap}>
                    <Icon icon={['fad', 'lock-keyhole']} size={20} color="#EE6C4C" />
                  </View>
                  <View style={s.securityRowText}>
                    <Text style={s.securityRowLabel}>4-Digit PIN</Text>
                    <Text style={s.securityRowDesc}>Quick lock screen protection</Text>
                  </View>
                  <View style={[s.securityCheck, pinOptIn && s.securityCheckActive]}>
                    {pinOptIn && <Icon icon={['fas', 'check']} size={11} color="#fff" />}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={s.disclaimerFooter}
                  onPress={() => setDisclaimerVisible(true)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel="Read medical disclaimer"
                >
                  <Icon icon={['fas', 'circle-info']} size={12} color={theme.colors.muted} />
                  <Text style={s.disclaimerFooterText}>
                    Eleplan is not a medical device.{' '}
                    <Text style={s.disclaimerFooterLink}>Read disclaimer</Text>
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Notification permission status — the native OS prompt is
                triggered by the footer "Continue" button (see handleNext).
                This area just shows current status / loading feedback. */}
            {slide.action === 'notifications' && (
              <View style={s.notifRow}>
                {notifStatus === 'granted' ? (
                  <View style={s.notifGranted}>
                    <Icon icon={['fas', 'circle-check']} size={18} color="#10B981" />
                    <Text style={s.notifGrantedText}>Notifications enabled</Text>
                  </View>
                ) : notifStatus === 'loading' ? (
                  <ActivityIndicator color="#F59E0B" />
                ) : notifStatus === 'denied' ? (
                  <Text style={s.notifSkipText}>You can enable this later in Settings</Text>
                ) : null}
              </View>
            )}

            {/* Location permission status — the disclosure sheet (and in
                turn the native OS prompt) is triggered by the footer
                "Continue" button (see handleNext). This area just shows
                current status / loading feedback. */}
            {slide.action === 'location' && (
              <View style={s.notifRow}>
                {locationStatus === 'granted' ? (
                  <View style={s.notifGranted}>
                    <Icon icon={['fas', 'circle-check']} size={18} color="#10B981" />
                    <Text style={s.notifGrantedText}>Location sharing enabled</Text>
                  </View>
                ) : locationStatus === 'loading' ? (
                  <ActivityIndicator color="#0EA5E9" />
                ) : locationStatus === 'denied' ? (
                  <Text style={s.notifSkipText}>You can enable this later in Settings</Text>
                ) : null}
              </View>
            )}

            {/* Persona / interests selection */}
            {slide.action === 'interests' && (
              <View style={s.personaGrid}>
                {PERSONA_OPTIONS.map((opt) => {
                  const active = personaSelection.includes(opt.key);
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      style={[s.personaChip, active && s.personaChipActive, active && { borderColor: opt.color, backgroundColor: opt.color + '18' }]}
                      onPress={() => handlePersonaToggle(opt.key)}
                      activeOpacity={0.8}
                    >
                      <Icon icon={opt.icon as any} size={14} color={active ? opt.color : theme.colors.muted} />
                      <Text style={[s.personaChipText, active && s.personaChipTextActive, active && { color: opt.color }]}>
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
          </View>
        ))}
      </ScrollView>

      {/* Dots + step counter */}
      <View style={s.dotsRow}>
        <Text style={[s.stepCounter, { color: theme.colors.muted }]}>
          {activeIndex + 1} of {slides.length}
        </Text>
        <View style={s.dotsInner}>
          {slides.map((_, i) => (
            <View
              key={i}
              style={[
                s.dot,
                {
                  backgroundColor: i === activeIndex ? theme.colors.primary : theme.colors.border,
                  width: i === activeIndex ? 20 : 8,
                },
              ]}
            />
          ))}
        </View>
      </View>

      {/* Footer */}
      <View style={s.footer}>
        {isLast ? (
          <>
            {mode === 'replay' ? (
              <TouchableOpacity
                style={s.primaryBtn}
                activeOpacity={0.85}
                onPress={async () => { await markOnboardingComplete(); onDone(); }}
              >
                <Text style={s.primaryBtnText}>Done</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity
                  style={s.primaryBtn}
                  activeOpacity={0.85}
                  onPress={async () => { await markOnboardingComplete(); onDone('login'); }}
                >
                  <Text style={s.primaryBtnText}>Sign In</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={s.secondaryBtn}
                  activeOpacity={0.85}
                  onPress={async () => { await markOnboardingComplete(); onDone('register'); }}
                >
                  <Text style={s.secondaryBtnText}>Create Account</Text>
                </TouchableOpacity>
              </>
            )}
          </>
        ) : slides[activeIndex]?.action === 'interests' ? (
          <>
            <TouchableOpacity style={s.primaryBtn} activeOpacity={0.85} onPress={handlePersonaContinue}>
              <Text style={s.primaryBtnText}>
                {personaSelection.length > 0 ? `Continue with ${personaSelection.length} selected` : 'Continue'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.secondaryBtn} activeOpacity={0.85} onPress={() => goTo(activeIndex + 1)}>
              <Text style={s.secondaryBtnText}>Skip</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity style={s.primaryBtn} activeOpacity={0.85} onPress={handleNext}>
            <Text style={s.primaryBtnText}>
              {slides[activeIndex]?.action === 'security'
              ? (biometricOptIn || pinOptIn ? "Let's Go" : 'Skip for Now')
              : 'Continue'}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      <MedicalDisclaimerModal
        visible={disclaimerVisible}
        onClose={() => setDisclaimerVisible(false)}
      />
      <LocationSharingDisclosureSheet
        visible={locationDisclosureVisible}
        mode="consent"
        onClose={() => setLocationDisclosureVisible(false)}
        onEnable={async () => {
          setLocationDisclosureVisible(false);
          await handleLocationPermissions();
          // Auto-advance from the location slide once the user has answered
          // the OS prompt — matches the notifications-slide flow.
          if (slides[activeIndex]?.action === 'location') {
            goTo(activeIndex + 1);
          }
        }}
      />
    </SafeAreaView>
  );

  if (!isDesktop) return chrome;

  // Desktop wrapper — center the onboarding chrome inside a max-width
  // column. The parent widget frame (DesktopLayout) provides the actual
  // card surface; this layer is just a transparent column constraint.
  return (
    <View style={s.desktopFrame}>
      <View style={s.desktopColumn}>{chrome}</View>
    </View>
  );
}
